import ClosedAlt from '@forwardflow/assets/svg/ClosedAlt'
import FaCalendarAlt from '@forwardflow/assets/svg/FaCalendarAlt'
import FaUserPlus from '@forwardflow/assets/svg/FaUserPlus'
import FaUsers from '@forwardflow/assets/svg/FaUsers'
import { useGetDashboardChartsLeadSource } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import { useSearchParams } from 'react-router-dom'

const iconMap: Record<string, JSX.Element> = {
   users: <FaUsers height={24} width={24} />,
   target: <FaUserPlus height={24} width={24} />,
   calendar: <FaCalendarAlt height={24} width={24} />,
   checkCircle: <ClosedAlt height={24} width={24} />,
   zap: (
      <img
         src="./images/ff.png"
         alt="Forward Flow Icon"
         width={24}
         height={24}
         className="scale-110"
      />
   ),
}

export const LeadStatsCards = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const {
      data: DashboardChartsLeadSource,
      isLoading: DashboardChartsLeadSourceLoading,
   } = useGetDashboardChartsLeadSource({
      timePeriod: timePeriod as TimePeriod,
   })

   const stats =
      DashboardChartsLeadSource?.stats?.map((stat: any) => ({
         label: stat.displayTitle,
         value: stat.unit
            ? `${stat.value ?? 0}${stat.unit}`
            : stat.value != null
            ? stat.value.toLocaleString()
            : '0',
         icon: iconMap[stat.icon] || null,
      })) || []

   if (DashboardChartsLeadSourceLoading) {
      return (
         <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
            {[...Array(5)].map((_, idx) => (
               <div
                  key={idx}
                  className="relative flex flex-col items-center justify-center py-4 rounded-lg lg:py-4 bg-white/5 animate-pulse"
               >
                  <div className="w-6 h-6 mb-3 rounded-full bg-white/20" />
                  <div className="w-20 h-4 mb-2 rounded bg-white/20" />
                  <div className="w-16 h-6 rounded bg-white/20" />
               </div>
            ))}
         </div>
      )
   }

   return (
      <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
         {stats.map((stat, idx) => {
            const isForwardFlowCoverage =
               idx === stats.length - 1 &&
               stat.label?.toLowerCase().includes('forward flow coverage') &&
               (!stat.value || stat.value === '0')

            if (isForwardFlowCoverage) {
               return (
                  <div
                     key={stat.label}
                     className="relative flex flex-col items-center justify-center py-4 rounded-lg lg:py-5 bg-white/5"
                  >
                     <img
                        src="./images/ff.png"
                        alt="Forward Flow Icon"
                        width={24}
                        height={24}
                        className="mb-3 scale-110 grayscale"
                     />
                     <div className="mb-2 font-medium text-center text-white/70">
                        Forward Flow Coverage
                     </div>
                     <div className="text-center text-white/70 text-[14px]">
                        No Forward Flow data available yet
                     </div>
                  </div>
               )
            }

            return (
               <div
                  key={stat.label}
                  className="relative flex flex-col items-center justify-center py-4 rounded-lg lg:pt-6 lg:pb-5 bg-white/5"
               >
                  {stat.icon}
                  <div className="mt-3 mb-2 text-center text-white/70">
                     {stat.label}
                  </div>
                  <div className="text-[24px] font-semibold text-white text-center">
                     {stat.value}
                  </div>
               </div>
            )
         })}
      </div>
   )
}
